import { formatExamDateModal, useExamEventDetails } from '@exam-center/exam-event-management';
import {
  GetExamEventExamineeCountSummaryDocument,
  SearchExamEventExamineesDocument,
  SearchExamineeStatusesInExamEventDocument,
  SearchExamineeStatusesInExamEventWithDataLoaderSessionExamineeDocument,
  useCancelExamEventExamineeMutation,
  useGetExamEventExamineeByIdLazyQuery,
} from '@exam-center/graphql';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Flex,
  Form,
  FormSelection,
  FormTextarea,
  Text,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver } from '@shared/utils';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { CANCELATION_REASON_CATEGORIES } from '../../constants';

import { CancelExamEventExamineeFormSchema, type CancelExamEventExamineeFormInput } from './schema';

interface CancelExamEventExamineeModalProps {
  open: boolean;
  onClose: () => void;
  examEventExamineeId: string;
  examCourseCode: string;
}

export default function CancelExamEventExamineeModal(
  props: Readonly<CancelExamEventExamineeModalProps>,
) {
  const { open: isOpen, onClose, examEventExamineeId, examCourseCode } = props;

  const {
    examEvent: { examStartedAt, examEndedAt },
  } = useExamEventDetails();

  const { showSnackbar } = useSnackbar();

  const formContext = useForm<CancelExamEventExamineeFormInput>({
    resolver: zodResolver(CancelExamEventExamineeFormSchema),
    defaultValues: { cancelationReasonCategory: undefined, remark: '' },
  });

  const { handleSubmit, reset, formState } = formContext;

  const [getExaminee, { data: examineeData }] = useGetExamEventExamineeByIdLazyQuery();

  useEffect(() => {
    if (isOpen) {
      getExaminee({
        variables: { getExamEventExamineeByIdInput: { id: examEventExamineeId } },
      });
    }
  }, [isOpen, examEventExamineeId, getExaminee]);

  const examinee = examineeData?.ecGetExamEventExamineeById?.examEventExaminee;
  const fullName = examinee
    ? [
        examinee.examinee.title,
        examinee.examinee.firstName,
        examinee.examinee.middleName,
        examinee.examinee.lastName,
      ]
        .filter(Boolean)
        .join(' ')
    : '-';
  const idNumber = examinee?.examinee.idNumber ?? '-';

  const [cancelExaminee, { loading }] = useCancelExamEventExamineeMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      SearchExamEventExamineesDocument,
      SearchExamineeStatusesInExamEventDocument,
      SearchExamineeStatusesInExamEventWithDataLoaderSessionExamineeDocument,
    ],
  });

  const handleCancel = async (data: CancelExamEventExamineeFormInput) => {
    await cancelExaminee({
      variables: {
        cancelExamEventExamineeInput: {
          examEventExamineeId,
          remark: data.remark,
          cancelationReason: data.cancelationReasonCategory,
        },
      },
      onCompleted: () => {
        showSnackbar({ message: 'ยกเลิกผู้เข้าสอบเรียบร้อยแล้ว', variant: 'success' });
        onClose();
      },
      onError: () => {
        showSnackbar({
          message: 'ยกเลิกผู้เข้าสอบไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
          variant: 'error',
        });
      },
      refetchQueries: [GetExamEventExamineeCountSummaryDocument],
      awaitRefetchQueries: true,
    });
  };

  return (
    <FormProvider {...formContext}>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth='sm'
        fullWidth
        disableEscapeKeyDown={formState.isSubmitting}
        slotProps={{
          paper: {
            component: Form,
            onSubmit: handleSubmit(handleCancel),
          },
          transition: {
            onExited: () => reset(),
          },
        }}
      >
        <DialogTitle onClose={onClose} loading={formState.isSubmitting}>
          ยกเลิกผู้เข้าสอบ
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ display: 'flex', flexDirection: 'column', gap: (theme) => theme.spacing(4) }}
        >
          <Flex
            sx={(theme) => ({
              flexDirection: 'column',
              border: `1px solid`,
              borderColor: theme.palette.divider,
              backgroundColor: theme.palette.grey[50],
              borderRadius: theme.shape.borderRadius,
              p: theme.spacing(4),
              gap: theme.spacing(4),
            })}
          >
            <Flex>
              <Flex sx={{ flexDirection: 'column', flex: 1, gap: (theme) => theme.spacing(1) }}>
                <Text variant='body2' color='text.secondary'>
                  ชื่อ-นามสกุล
                </Text>
                <Text sx={{ fontWeight: 'bold' }}>{fullName}</Text>
              </Flex>
              <Flex sx={{ flexDirection: 'column', flex: 1, gap: (theme) => theme.spacing(1) }}>
                <Text variant='body2' color='text.secondary'>
                  ID / Passport No.
                </Text>
                <Text sx={{ fontWeight: 'bold' }}>{idNumber}</Text>
              </Flex>
            </Flex>
            <Flex>
              <Flex sx={{ flexDirection: 'column', flex: 1, gap: (theme) => theme.spacing(1) }}>
                <Text variant='body2' color='text.secondary'>
                  หลักสูตรที่สมัคร
                </Text>
                <Text sx={{ fontWeight: 'bold' }}>{examCourseCode}</Text>
              </Flex>
              <Flex sx={{ flexDirection: 'column', flex: 1, gap: (theme) => theme.spacing(1) }}>
                <Text variant='body2' color='text.secondary'>
                  วัน-เวลาที่สอบ
                </Text>
                <Text sx={{ fontWeight: 'bold' }}>
                  {formatExamDateModal(examStartedAt, examEndedAt)}
                </Text>
              </Flex>
            </Flex>
          </Flex>

          <Text>
            คุณกำลังจะ{' '}
            <Text component='span' sx={{ fontWeight: 'bold' }}>
              &quot;ยกเลิกผู้เข้าสอบ&quot;
            </Text>{' '}
            กรุณาตรวจสอบความถูกต้องก่อนดำเนินการ
          </Text>

          <FormSelection
            name='cancelationReasonCategory'
            label='สาเหตุที่ยกเลิก'
            required
            options={[...CANCELATION_REASON_CATEGORIES]}
            placeholder='กรุณาเลือกสาเหตุที่ยกเลิก'
            fullWidth
          />
          <FormTextarea
            name='remark'
            label='กรุณาระบุสาเหตุ'
            required
            fullWidth
            minRows={1}
            placeholder='เช่น เกิดเหตุสุดวิสัย, ขอเลื่อนสอบ'
          />
        </DialogContent>
        <DialogActions>
          <Button
            size='large'
            variant='outlined'
            color='secondary'
            onClick={onClose}
            disabled={formState.isSubmitting || loading}
          >
            ยกเลิก
          </Button>
          <Button
            size='large'
            type='submit'
            color='error'
            loading={formState.isSubmitting || loading}
          >
            ยกเลิกผู้เข้าสอบ
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
